/**
 * Микроуроки — библиотека управляющего (§10.14, D59).
 *
 * Одиннадцать уроков заводятся сидингом черновиками: по одному на каждый
 * показатель наставника и четыре общих о сервисе. Сотрудник получает
 * только опубликованные — урок выбирается по его цели роста, а без цели
 * по отставанию или общий.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, ErrorState, Loading } from '@shell/index';
import { studyApi } from '../api';

const KEY = ['admin-micro'];

export function EditorMicro() {
  const queryClient = useQueryClient();
  const lessons = useQuery({ queryKey: KEY, queryFn: studyApi.adminMicroLessons });
  const toggle = useMutation({
    mutationFn: ({ id, published }: { id: number; published: boolean }) =>
      published ? studyApi.unpublishMicro(id) : studyApi.publishMicro(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: KEY }),
  });

  if (lessons.isPending) return <Loading />;
  if (lessons.error || !lessons.data)
    return <ErrorState error={lessons.error} onRetry={() => lessons.refetch()} />;

  const rows = lessons.data.lessons;
  const published = rows.filter((row) => row.status === 'published').length;

  return (
    <>
      <p className="faint">
        Микроуроков: {rows.length}, опубликовано {published}. Каждый день в 11:00 сотрудник с
        наставником получает один опубликованный урок — по своей цели роста, а без цели по
        наибольшему отставанию или общий.
      </p>
      {rows.map((lesson) => {
        const isPublished = lesson.status === 'published';
        return (
          <Card key={lesson.id} title={lesson.topic}>
            <p className="faint">
              {isPublished ? 'опубликован' : 'черновик'}
              {lesson.metric_title ? ` · для цели «${lesson.metric_title}»` : ' · общий'}
            </p>
            <p>{lesson.body}</p>
            <ul>
              {lesson.good_phrases.map((phrase) => (
                <li key={phrase}>{phrase}</li>
              ))}
            </ul>
            <Button
              small
              variant={isPublished ? 'quiet' : 'primary'}
              disabled={toggle.isPending}
              onClick={() => toggle.mutate({ id: lesson.id, published: isPublished })}
            >
              {isPublished ? 'Снять с публикации' : 'Опубликовать'}
            </Button>
          </Card>
        );
      })}
    </>
  );
}
